<template>
  <ModalDialog
    ref="dialog"
    :title="title"
    icon="fa-th-large"
    :showFooter="true"
    :submitting="submitting"
    :submitDisabled="!canSubmit"
    :showSubmit="editing"
    :closeTitle="closeTitle"
    :submitTitle="submitTitle"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="form" :labelColumnSize="3">
      <TabbedBox>
        <template v-slot:tablist>
          <Tab :first="true" tabid="tab-info">
            <i class="far fa-th-large"></i> Sheet
          </Tab>
          <Tab tabid="tab-allocations" v-if="!isCreateMode">
            <i class="far fa-clipboard-list"></i> Allocations
          </Tab>
          <Tab tabid="tab-developer" v-if="!isCreateMode && isDeveloperUser">
            <i class="far fa-code"></i> Developer
          </Tab>
        </template>

        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-info">
            <div class="float-right">
              <ActionDropMenu>
                <DropdownMenuItem v-if="isOffcutSelected" icon="fa-flag" @click="flagSelectedOffcutIssue">Flag Offcut Issue</DropdownMenuItem>
              </ActionDropMenu>
            </div>
            <ContentTitle>Sheet Info</ContentTitle>
            <FormRow label="Stock">
              <FormStaticLabel v-if="!isCreateMode || !editing">{{optimiserSheet.stock.code}} - {{optimiserSheet.stock.name}}</FormStaticLabel>
              <FormInputStockCode v-if="isCreateMode && editing" :stockType="InventoryStockTypes.glass" v-model="optimiserSheet.stockId" />
            </FormRow>
            <FormRow label="Sheet Size">
              <FormSelect :options="glassLevelLookup" v-model="optimiserSheet.glassLevelId" v-if="hasStockSelected && !loadingStock && !loadingStockError && editing"></FormSelect>
              <FormStaticLabel v-if="hasStockSelected && !loadingStock && !loadingStockError && !editing">{{sheetSizeDisplay}}</FormStaticLabel>

              <FormStaticLabel v-if="!hasStockSelected && !loadingStock && !loadingStockError" class="text-muted">Select stock</FormStaticLabel>
              <Spinner v-if="loadingStock" />
              <span v-if="loadingStockError" class="text-danger">{{loadingStockError}}</span>
            </FormRow>
            <FormRow label="Custom Size" :fieldCount="2" v-show="hasStockSelected && isCustomSizeSelected && !loadingStock && !loadingStockError && editing">
              <template v-slot:field1>
                <FormInputText type="number" prepend="Height" addon="mm" v-model="optimiserSheet.height" />
              </template>
              <template v-slot:field2>
                <FormInputText type="number" prepend="Width" addon="mm" v-model="optimiserSheet.width" />
              </template>
            </FormRow>
            <FormRow label="Custom Grain" v-show="hasStockSelected && isCustomSizeSelected && isStockUsingGrain && !loadingStock && !loadingStockError">
              <FormSelect :options="StockGrainDirectionTypeLookup" v-model="optimiserSheet.grainDirection" v-if="editing" field="grainDirection"></FormSelect>
              <FormStaticLabel v-if="!editing">{{formatLookupName(optimiserSheet.grainDirection, StockGrainDirectionTypeLookup)}}</FormStaticLabel>
            </FormRow>
            <FormRow label="Grain" v-if="hasStockSelected && !isCustomSizeSelected && isStockUsingGrain && !loadingStock && !loadingStockError && selectedGlassLevelInfo">
              <FormStaticLabel>{{formatLookupName(selectedGlassLevelInfo.grainDirection, StockGrainDirectionTypeLookup)}}</FormStaticLabel>
            </FormRow>
            <FormRow label="Offcut Info" v-if="selectedOffcut">
              <div class="row">
                <div class="col-2">Barcode:</div>
                <div class="col-10">{{selectedOffcut.barcode}}</div>
              </div>
              <div class="row">
                <div class="col-2">Rack:</div>
                <div class="col-10">{{formatHyphenEmptyValue(selectedOffcut.offcutRack)}}</div>
              </div>
              <div class="row">
                <div class="col-2">Row:</div>
                <div class="col-10">{{formatHyphenEmptyValue(selectedOffcut.offcutRow)}}</div>
              </div>
              <div class="row">
                <div class="col-2">Comments:</div>
                <div class="col-10">{{formatHyphenEmptyValue(selectedOffcut.offcutComments)}}</div>
              </div>
            </FormRow>
            <FormRow label="Quantity" v-if="isCreateMode && !selectedOffcut">
              <FormInputText type="number" v-model="createQuantity" field="quantity" placeholder="Quantity" />
            </FormRow>

            <FormHorizontalRule />
            <ContentTitle>Trim</ContentTitle>
            <ContentSubTitle>Spacing from edges of the sheet</ContentSubTitle>

            <FormRow label="Top Trim">
              <FormInputText type="number" addon="mm" v-model="optimiserSheet.trimTop" v-if="editing" />
              <FormStaticLabel v-if="!editing">{{optimiserSheet.trimTop}}mm</FormStaticLabel>
            </FormRow>
            <FormRow label="Right Trim">
              <FormInputText type="number" addon="mm" v-model="optimiserSheet.trimRight" v-if="editing" />
              <FormStaticLabel v-if="!editing">{{optimiserSheet.trimRight}}mm</FormStaticLabel>
            </FormRow>
            <FormRow label="Bottom Trim">
              <FormInputText type="number" addon="mm" v-model="optimiserSheet.trimBottom" v-if="editing" />
              <FormStaticLabel v-if="!editing">{{optimiserSheet.trimBottom}}mm</FormStaticLabel>
            </FormRow>
            <FormRow label="Left Trim">
              <FormInputText type="number" addon="mm" v-model="optimiserSheet.trimLeft" v-if="editing" />
              <FormStaticLabel v-if="!editing">{{optimiserSheet.trimLeft}}mm</FormStaticLabel>
            </FormRow>
          </TabPage>

          <TabPage tabid="tab-allocations">
            <ContentTitle>Allocations</ContentTitle>
            <OptimiserAllocationList :allocationList="optimiserSheet.optimiserSheetAllocations" :linksEnabled="false" />
          </TabPage>

          <TabPage tabid="tab-developer" v-if="!isCreateMode && isDeveloperUser">
            <ContentTitle>Sheet Data</ContentTitle>
            id: {{optimiserSheet.id}}<br />
            <ContentTitle>Sheet Nodes</ContentTitle>
            <NestedList :listTree="generateDeveloperNodeOutput()" :collapsable="true">
              <template v-slot:itemSlot="slotProps">
                {{slotProps.item.info}}
              </template>
            </NestedList>
            
          </TabPage>

        </template>
      </TabbedBox>
    </FormGroup>
  </ModalDialog>
</template>

<style scoped>

</style>

<script>
import _ from 'lodash'

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'

import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'
import OptimiserAllocationList from '@/modules/optimiser/components/optimiserAllocationList'
import NestedList from '@/components/table/nestedList.vue'
import GlassOffcutStationDialog from '@/apps/glass-production-station/src/views/glassOffcutStationDialog'

import { InventoryStockTypes, StockGrainDirectionTypes, StockGrainDirectionTypeLookup } from '@/store/types'

export default {
  name: 'OptimiserSheetEditDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog,
    FormInputStockCode,
    OptimiserAllocationList,
    NestedList
  },
  props: {
    isCreateMode: Boolean,
    productionStageId: Number,
    productionStationId: Number,
    editing: Boolean,
    editingSheetObj: Object
  },
  data() {
    return {
      form: {},
      submitting: false,

      createQuantity: 1,
      optimiserSheet: {
        stock: {},
        stockId: null,
        glassLevelId: 0,
        width: 0,
        height: 0,
        trimTop: 0,
        trimRight: 0,
        trimBottom: 0,
        trimLeft: 0,
        grainDirection: StockGrainDirectionTypes.none,
        isRotated: false
      },
      loadingStock: false,
      loadingStockError: '',
      glassLevelLookup: []
    }
  },
  watch: {
    'optimiserSheet.stockId': function() {
      this.requestStockData()
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
    this.StockGrainDirectionTypeLookup = StockGrainDirectionTypeLookup
  },
  mounted() {
  },
  computed: {
    title() {
      if(this.isCreateMode)
        return 'New Sheet'
      if(!this.editing)
        return 'Sheet'
      return 'Edit Sheet'
    },
    submitTitle() {
      return this.isCreateMode ? 'Create' : 'Update'
    },
    closeTitle() {
      return this.editing ? 'Cancel' : 'Close'
    },
    hasStockSelected() {
      return this.optimiserSheet.stockId != null
    },
    isStockUsingGrain() {
      if(!this.hasStockSelected)
        return false
      return this.optimiserSheet.stock.isStockUsingGrain
    },
    isCustomSizeSelected() {
      return this.optimiserSheet.glassLevelId == 0
    },
    isOffcutSelected() {
      return this.selectedOffcut != null
    },
    canSubmit() {
      if(!this.hasStockSelected || this.loadingStock)
        return false
      return true
    },
    selectedGlassLevelInfo() {
      if(this.hasStockSelected) {

        let glassLevelInfo = this.glassLevelLookup.reduce((curValue, glassSize) => {
          if(curValue)
            return curValue
          return (glassSize.children || []).find(lookup => lookup.value == this.optimiserSheet.glassLevelId)
        }, null)

        return glassLevelInfo
      }
      return null
    },
    selectedOffcut() {
      var selectedInfo = this.selectedGlassLevelInfo
      if(selectedInfo && selectedInfo.isOffcut)
        return selectedInfo

      return null
    },
    sheetSizeDisplay() {
      let width = this.optimiserSheet.width
      let height = this.optimiserSheet.height

      if(!this.hasStockSelected)
        return ''

      if(!this.isCustomSizeSelected) {
        let glassLevelInfo = this.glassLevelLookup.reduce((curValue, glassSize) => {
          if(curValue)
            return curValue
          return (glassSize.children || []).find(lookup => lookup.value == this.optimiserSheet.glassLevelId)
        }, null)

        if(glassLevelInfo) {
          width = glassLevelInfo.width
          height = glassLevelInfo.height
        } else {
          return ''
        }
      }

      return `${height} x ${width}`
    }
  },
  methods: {
    show(params) {
      this.form = {}
      this.submitting = false

      if(params && !this.isCreateMode) {
        this.optimiserSheet = _.cloneDeep(params)
        this.optimiserSheet.glassLevelId = parseInt(this.optimiserSheet.glassLevelId || 0)
      }

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.form = this.clearFormResponse()
      this.submitting = true

      // Force quantity of 1 if selecting an offcut to create a new optimiser sheet
      if(this.isCreateMode && this.isOffcutSelected)
        this.createQuantity = 1

      let updateData = {
        stockId: this.optimiserSheet.stockId,
        glassLevelId: this.optimiserSheet.glassLevelId,
        quantity: this.createQuantity,
        trimTop: this.optimiserSheet.trimTop,
        trimRight: this.optimiserSheet.trimRight,
        trimBottom: this.optimiserSheet.trimBottom,
        trimLeft: this.optimiserSheet.trimLeft,
        isRotated: this.optimiserSheet.isRotated
      }

      if(this.isCustomSizeSelected) {
        updateData.width = this.optimiserSheet.width
        updateData.height = this.optimiserSheet.height

        if(updateData.isRotated) {
          updateData.width = this.optimiserSheet.height
          updateData.height = this.optimiserSheet.width
        }
      }

      if(this.isCustomSizeSelected && this.isStockUsingGrain)
        updateData.grainDirection = this.optimiserSheet.grainDirection

      // Submit through the server
      if(this.isCreateMode) {
        this.$apiService.glassProductionStationService.createSheet(this.productionStationId, updateData)
        .then(response => {
          this.modalClosed(response.data)
          this.hide()
        })
        .catch(err => {
          this.form = this.processFormResponse(err)
        })
        .finally(() => {
          this.submitting = false
        })

      } else {
        this.$apiService.glassProductionStationService.updateSheet(this.productionStationId, this.optimiserSheet.id, updateData)
        .then(response => {
          this.modalClosed(response.data)
          this.hide()
        })
        .catch(err => {
          this.form = this.processFormResponse(err)
        })
        .finally(() => {
          this.submitting = false
        })
      }
    },
    requestStockData() {
      if(this.optimiserSheet.stockId == null) {
        this.glassLevelLookup = []
        this.optimiserSheet.glassLevelId = 0
        this.optimiserSheet.stock = {}
        return
      }

      this.loadingStock = true

      this.$apiService.inventoryService.getStock(this.optimiserSheet.stockId)
      .then(response => {
        this.optimiserSheet.stock = _.cloneDeep(response.data)

        let foundGlassLevel = false
        let defaultGlassLevelId = null

        // Calculate default / id check
        response.data.glassSizeList.forEach(glassSize => {
          let validLevels = glassSize.glassLevels.filter(glassLevel => glassLevel.isActive && (!glassLevel.isOffcut || glassLevel.isOffcutAvailable))

          if(validLevels.length > 0) {
            if(!foundGlassLevel) {
              if(validLevels.find(glassLevel => glassLevel.id == this.optimiserSheet.glassLevelId)) {
                foundGlassLevel = true
              }
            }

            if(!defaultGlassLevelId)
              defaultGlassLevelId = validLevels[0].id
          }
        })

        // Build the glass size list
        this.glassLevelLookup = []
        this.glassLevelLookup = _.concat(this.glassLevelLookup, response.data.glassSizeList.reduce((list, glassSize) => {
          if(!glassSize.isActive || glassSize.glassLevels.length == 0)
            return list
          list.push({
            name: `${glassSize.height}x${glassSize.width}`,
            children: glassSize.glassLevels.filter(glassLevel => glassLevel.isActive && (!glassLevel.isOffcut || glassLevel.isOffcutAvailable)).map(glassLevel => {
              let levelName = `${glassLevel.height}x${glassLevel.width}`
              if(glassLevel.isOffcut)
                levelName += ` - Offcut`
              else
                levelName += ` - ${glassLevel.qtyOnHand} Sheets`

              return {
                value: glassLevel.id,
                name: levelName,
                width: glassLevel.width,
                height: glassLevel.height,
                isOffcut: glassLevel.isOffcut,
                barcode: glassLevel.barcode,
                offcutRack: glassLevel.offcutRack,
                offcutRow: glassLevel.offcutRow,
                offcutComments: glassLevel.offcutComments,
                grainDirection: glassSize.grainDirection
              }
            })
          })
          return list
        }, []))
        this.glassLevelLookup.push({
          value: 0,
          name: 'Custom Size'
        })

        // If current selected glass level not in list (eg, stock was changed, then reset)
        if(!foundGlassLevel)
          this.optimiserSheet.glassLevelId = 0

        // Default glass item if none selected
        // Except if editing a custom size sheet
        if(defaultGlassLevelId && this.optimiserSheet.glassLevelId == null && (this.isCreateMode || !this.optimiserSheet.isCustomSize))
          this.optimiserSheet.glassLevelId = defaultGlassLevelId

      })
      .catch(err => {
        console.error(err)
        this.loadingStockError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loadingStock = false
      })
    },

    flagSelectedOffcutIssue() {
      this.modalClosed(null)

      this.hide(() => {

        this.displayModal(GlassOffcutStationDialog, {
          id: parseInt(this.optimiserSheet.glassLevelId),
          productionStageId: this.productionStageId,
          productionStationId: this.productionStationId,
          showOffcutIssueTab: true
        }, {}, { mountBody: true })
        .then(() => {
        })
      });
    },

    // Developer: Generate sheet node output structure
    generateDeveloperNodeOutput() {
      if(!this.editingSheetObj || !this.editingSheetObj.rootNode)
        return []

      return this._generateDeveloperOutput(this.editingSheetObj.rootNode)
    },
    _generateDeveloperOutput(node, level = 0) {
      if(!node)
        return []

      let nodeOutput = {
        id: node.id,
        type: node.type,
        x: node.x,
        y: node.y,
        width: node.width,
        height: node.height,
        panelId: node.panelId,
        splitDir: node.splitDir,
        level: level,
        collapsed: true,
        children: [],
        info: `${node.id}:${node.type} : {x:${node.x}, y: ${node.y}, w: ${node.width}, h: ${node.height}, split: ${node.splitDir}} `
      }
      if(node.type == 'panel')
        nodeOutput.info += ` - panelID: ${node.panelId}`

      nodeOutput.children = _.concat(nodeOutput.children, this._generateDeveloperOutput(node.childNodes[0], level+1))
      nodeOutput.children = _.concat(nodeOutput.children, this._generateDeveloperOutput(node.childNodes[1], level+1))

      return [nodeOutput]
    }
  }
}
</script>